// Enhanced Romanian Company Data Service - Portable API integration
// Handles ANAF (Romanian tax authority) and InfoCUI API calls for company data

import { ANAFCompanyData } from "../types";
import { EntityData } from "../types/entity";
import { supabase } from "@/integrations/supabase/client";

import { loadFinancialData } from "@/utils/database";
import { processCuiInput } from "@/utils/cui-utils";
import { convertRonToEur } from "@/utils/currency-utils";
export interface CompanyBasicInfo {
  cui: string;
  denumire: string;
  caenPrincipal: string;
  caenSecundare?: string;
  adresa: string;
  codPostal: string;
  telefon: string;
}

export interface CompanyFinancialInfo {
  numarSalariati: string;
  cifraAfaceri: string;
  totalActive: string;
}

export interface CombinedCompanyData
  extends CompanyBasicInfo,
    CompanyFinancialInfo {}

// Configuration options for the service
export interface ServiceConfig {
  proxyUrl?: string;
  infoCuiApiKey?: string;
  financialDataYear?: string;
}

const DEFAULT_CONFIG: ServiceConfig = {
  proxyUrl: "https://corsproxy.io/?",
  infoCuiApiKey: "eb82cdc2b41ebb3925b522ed2df18f6b803eb5ce", // Demo key - configurable in production
  // TODO: Make financial data year configurable in admin panel
  // Currently auto-calculates previous year as INFOCUI doesn't return data from 2024
  financialDataYear: (new Date().getFullYear() - 1).toString(),
};

// Exchange rate for RON to EUR conversion (December 2024)
const RON_TO_EUR_RATE = 4.97;

export interface CompanyDataResult {
  success: boolean;
  entityData?: any;
  anafData?: any;
  infocuiData?: any;
  error?: string;
  notFound?: boolean;
  hasFinancialData?: boolean;
  hasVatRegistration?: boolean;
  anafVatValidated?: boolean;
  originalCuiInput?: string;
  cleanCui?: string;
}

/**
 * Fetch company basic information from ANAF (Romanian National Agency for Fiscal Administration)
 * @param cui - Company Unique Identifier (CUI/CIF)
 * @param config - Optional configuration overrides
 * @returns Promise with company basic data from ANAF
 */
export const fetchCompanyDataFromANAF = async (
  cui: string,
  _config: ServiceConfig = {}
): Promise<any> => {
  const { data, error } = await supabase.functions.invoke(
    `api/anaf-company?cui=${encodeURIComponent(cui)}`,
    { method: "GET" }
  );

  if (error) {
    throw new Error(`ANAF API request failed: ${error.message}`);
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data;
};

/**
 * Fetch company financial information from InfoCUI API
 * @param cui - Company Unique Identifier (CUI/CIF)
 * @param config - Optional configuration overrides
 * @returns Promise with financial data from InfoCUI
 */
export const fetchFinancialDataFromInfoCUI = async (
  cui: string,
  config: ServiceConfig = {}
): Promise<any> => {
  // First, try to fetch from Supabase
  const { data: supabaseData, error: supabaseError } =
    await loadFinancialData(cui);

  console.log("Supabase fetch result:", { supabaseData, supabaseError });
  if (supabaseData) {
    console.log("📦 Supabase financial data found:", supabaseData);
    return {
      cui: cui,
      an: "2024",
      caen: supabaseData.caen,
      active_imob_total: supabaseData.active_imobilizate,
      active_circ_total: supabaseData.active_circulante,
      stocuri: supabaseData.stocuri,
      creante: supabaseData.creante,
      casa_cont_banca: supabaseData.casa_conturi_banci,
      chelt_avans: supabaseData.cheltuieli_in_avans,
      datorii: supabaseData.datorii,
      venit_avans: supabaseData.venituri_avans,
      provizioane: supabaseData.provizioane,
      capit_total_din_care: supabaseData.capital,
      capit_subscris_varsat: supabaseData.capital_subscris,
      patrimoniul_regiei: supabaseData.patrimoniul_regiei,
      cifra_afaceri_neta: supabaseData.cifra_de_afaceri_net,
      venituri_totale: supabaseData.venituri_totale,
      cheltuieli_totale: supabaseData.cheltuieli_totale,
      profit_brut: supabaseData.profit_brut,
      pierdere_bruta: supabaseData.pierdere_brut,
      profit_net: supabaseData.profit_net,
      pierdere_neta: supabaseData.pierdere_net,
      nr_mediu_salariati: supabaseData.nr_mediu_angajati,
      timestamp: 1757840979,
    };
  }
  if (supabaseError && supabaseError.code !== "PGRST116") {
    // Only throw if error is not 'No rows found'
    console.log(`Supabase error: ${supabaseError.message}`);
  }

  // If no data in Supabase, fallback to InfoCUI API
  const { proxyUrl, infoCuiApiKey, financialDataYear } = {
    ...DEFAULT_CONFIG,
    ...config,
  };
  const targetUrl = encodeURIComponent(
    `https://infocui.ro/system/api/finance?key=${infoCuiApiKey}&cui=${cui}&year=${financialDataYear}`
  );

  const response = await fetch(`${proxyUrl}${targetUrl}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(
      `InfoCUI API request failed: ${response.status} ${response.statusText}`
    );
  }

  const data = await response.json();
  console.log("🏢 InfoCUI API raw response:", {
    status: data.status,
    message: data.message,
    hasData: !!data.data,
    dataFields: data.data ? Object.keys(data.data) : [],
  });

  if (data.status === 200 && data.data) {
    console.log("💰 InfoCUI financial data extracted:", {
      nr_mediu_salariati: data.data.nr_mediu_salariati,
      active_imob_total: data.data.active_imob_total,
      active_circ_total: data.data.active_circ_total,
      cifra_afaceri_neta: data.data.cifra_afaceri_neta,
    });
    return data.data;
  }
  throw new Error("Financial data not found in InfoCUI database");
};

/**
 * Fetch ANAF VAT validation for a company
 * @param cui - Clean CUI (without RO prefix)
 * @param config - Optional configuration overrides
 * @returns Promise with VAT validation result
 */
export const fetchAnafVatValidation = async (
  cui: string,
  _config: ServiceConfig = {}
): Promise<{ isVatRegistered: boolean; validatedAt: string }> => {
  const { data, error } = await supabase.functions.invoke(
    `api/anaf-company?cui=${encodeURIComponent(cui)}`,
    { method: "GET" }
  );

  if (error) {
    throw new Error(`ANAF VAT API request failed: ${error.message}`);
  }

  const isVatRegistered = data?.date_generale?.platitor_tva;

  return {
    isVatRegistered: !!isVatRegistered,
    validatedAt: new Date().toISOString(),
  };
};

/**
 * Fetch combined company data from both ANAF and InfoCUI APIs
 * @param cuiInput - Company Unique Identifier (CUI/CIF) with optional RO prefix
 * @param config - Optional configuration overrides
 * @returns Promise with combined company data
 */
export const fetchCombinedCompanyData = async (
  cuiInput: string,
  config: ServiceConfig = {}
): Promise<CompanyDataResult> => {
  // Import CUI utilities
  // Process CUI input
  const cuiProcessing = processCuiInput(cuiInput);

  if (!cuiProcessing.isValid) {
    return {
      success: false,
      error: cuiProcessing.error || "Invalid CUI format",
      originalCuiInput: cuiProcessing.originalInput,
      cleanCui: cuiProcessing.cleanCui,
      hasVatRegistration: cuiProcessing.hasVatRegistration,
    };
  }

  const { originalInput, cleanCui, hasVatRegistration } = cuiProcessing;

  try {
    let anafData: ANAFCompanyData | null = null;
    let financialData: any = null;
    let anafVatValidation: any = null;
    let anafError: string | null = null;
    let infocuiError: string | null = null;
    let vatValidationError: string | null = null;

    // Try to fetch all APIs in parallel, but handle failures gracefully
    const promises = [
      fetchCompanyDataFromANAF(cleanCui, config).catch((error) => {
        anafError = error.message;
        return null;
      }),
      fetchFinancialDataFromInfoCUI(cleanCui, config).catch((error) => {
        infocuiError = error.message;
        return null;
      }),
      // Only validate VAT if user indicated VAT registration with RO prefix
      hasVatRegistration
        ? fetchAnafVatValidation(cleanCui, config).catch((error) => {
            vatValidationError = error.message;
            return null;
          })
        : Promise.resolve(null),
    ];

    const [anafResult, infocuiResult, vatValidationResult] =
      await Promise.all(promises);
    anafData = anafResult;
    financialData = infocuiResult;
    anafVatValidation = vatValidationResult;

    // If ANAF fails completely, return error
    if (!anafData) {
      return {
        success: false,
        error: anafError || "Failed to fetch basic company data from ANAF",
        notFound: true,
        originalCuiInput: originalInput,
        cleanCui: cleanCui,
        hasVatRegistration: hasVatRegistration,
      };
    }

    // Transform ANAF data
    const generalData = anafData.date_generale;
    const addressData = anafData.adresa_sediu_social;

    // Build address string from nested address object
    const addressParts = [
      addressData?.sdenumire_Strada,
      addressData?.snumar_Strada,
      addressData?.sdenumire_Localitate,
      addressData?.sdenumire_Judet,
    ].filter(Boolean);
    const fullAddress = addressParts.join(", ");

    // Process financial data - convert from RON to EUR if from InfoCUI
    const originalCifraAfaceriRon = parseInt(
      financialData?.cifra_afaceri_neta || "0"
    );
    const originalActiveRon = financialData
      ? parseInt(financialData.active_imob_total || "0") +
        parseInt(financialData.active_circ_total || "0")
      : 0;

    // Convert RON to EUR for EU compliance (NIS2 size classification)
    const cifraAfaceriEur = financialData
      ? convertRonToEur(originalCifraAfaceriRon)
      : 0;
    const activeEur = financialData ? convertRonToEur(originalActiveRon) : 0;

    // Transform and combine data from both sources
    const entityData: Partial<EntityData> = {
      cui: cleanCui,
      original_cui_input: originalInput,
      clean_cui: cleanCui,
      has_vat_registration: hasVatRegistration,
      anaf_vat_validated: !!anafVatValidation,
      anaf_vat_validated_at: anafVatValidation?.validatedAt || null,
      nume_entitate: generalData?.denumire || "",
      caen_principal: {
        cod: generalData?.cod_CAEN || "",
        denumire: "", // Will need to be filled from CAEN lookup
      },
      caen_secundar: [],
      cif: generalData?.nrRegCom || null,
      adresa: fullAddress || generalData?.adresa || "",
      telefon: generalData?.telefon || "",
      email: "",
      website: "",
      // Financial data - using EUR values for size classification (EU standard)
      nr_salariati: parseInt(financialData?.nr_mediu_salariati || "0"),
      cifra_afaceri: cifraAfaceriEur,
      active: activeEur,
      // Original RON values for reference
      original_cifra_afaceri_ron: originalCifraAfaceriRon,
      original_active_ron: originalActiveRon,
      currency_conversion_rate: financialData ? RON_TO_EUR_RATE : null,
      currency_converted_at: financialData ? new Date().toISOString() : null,
      // Metadata
      anaf_data_fetched_at: new Date().toISOString(),
      anaf_last_updated: generalData?.data || "",
      data_source: financialData ? "anaf+infocui" : "anaf",
      // TODO: Make this configurable in admin panel
      financial_data_year: financialData
        ? Number(DEFAULT_CONFIG.financialDataYear!)
        : null,
    };

    return {
      success: true,
      entityData,
      anafData,
      infocuiData: financialData,
      hasFinancialData: !!financialData,
      hasVatRegistration: hasVatRegistration,
      anafVatValidated: !!anafVatValidation,
      originalCuiInput: originalInput,
      cleanCui: cleanCui,
    };
  } catch (error) {
    console.error("Combined data fetch error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

/**
 * Utility function to format financial numbers with thousand separators
 * @param amount - Amount as string or number
 * @returns Formatted string with dots as thousand separators
 */
export const formatFinancialAmount = (amount: string | number): string => {
  if (!amount) return "";
  const numStr = typeof amount === "string" ? amount : amount.toString();
  return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

/**
 * Utility function to validate Romanian CUI format
 * @param cui - CUI to validate (should be cleaned)
 * @returns boolean indicating if CUI is valid
 */
export const validateCUI = (cui: string): boolean => {
  return /^\d{7,8}$/.test(cui);
};

/**
 * Utility function to process CUI input with RO prefix handling
 * @param cuiInput - Raw CUI input from user
 * @returns Processed CUI information
 */
export const processCUI = async (cuiInput: string) => {
  return processCuiInput(cuiInput);
};

/**
 * Extract domain from email address
 * @param email - Email address
 * @returns Domain part of the email
 */
export const extractDomainFromEmail = (email: string): string | null => {
  const emailRegex = /^[^@]+@(.+)$/;
  const match = email.match(emailRegex);
  return match ? match[1] : null;
};

/**
 * Fetch IP addresses from domain using DNS lookup
 * @param domain - Domain name to lookup
 * @param config - Optional configuration overrides
 * @returns Promise with array of IP addresses
 */
export const fetchIPAddressesFromDomain = async (
  domain: string,
  config: ServiceConfig = {}
): Promise<string[]> => {
  const response = await fetch(
    `https://dns.google/resolve?name=${domain}&type=A`,
    {
      method: "GET",
      headers: {
        Accept: "application/dns-json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(
      `DNS lookup failed: ${response.status} ${response.statusText}`
    );
  }

  const data = await response.json();

  // Check DNS response status (0 = success)
  if (data.Status !== 0) {
    throw new Error(`DNS resolution failed with status: ${data.Status}`);
  }

  if (data && data.Answer && Array.isArray(data.Answer)) {
    // Extract IP addresses from Google DNS API response
    return data.Answer.filter((record: any) => record.type === 1) // type 1 = A record
      .map((record: any) => record.data)
      .filter((ip: string) => ip && typeof ip === "string");
  }

  throw new Error("No A records found for domain");
};
